var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useStripes } from '@folio/stripes/core';
import { useReviewQueueCount } from '../hooks/useReviewQueueCount';
import { useErrorReportSummary } from '../hooks/useErrorReports';
import useAuthorizationContext from '../hooks/useAuthorizationContext';
import { formatReviewCountBadge } from '../utils/reviewCountBadge';
import { CORPUS_QUALITY_CAPABILITY } from '../utils/corpusQualityAccess';
import { ERROR_REPORTS_CAPABILITY } from '../utils/errorReportsAccess';
import { OAI_HARVEST_CAPABILITY, SETTINGS_CAPABILITY, VOCABULARY_TYPES_CAPABILITY, } from '../utils/settingsAccess';
var BASE_PATH = '/czna-authorities';
/** Route capability; role/type/stage/organization scoping stays on the backend. */
var REVIEW_CAPABILITY = 'czna-authorities.records.review-queue.get';
/** What `GET /review-queue/returned` (the Inbox) asks for — every user of the module has it. */
var INBOX_CAPABILITY = 'czna-authorities.records.collection.get';
var DASHBOARD_PATH = BASE_PATH;
var INBOX_PATH = "".concat(BASE_PATH, "/inbox");
var REVIEW_PATH = "".concat(BASE_PATH, "/review-queue");
var ERROR_REPORTS_PATH = "".concat(BASE_PATH, "/error-reports");
/** Drilling into settings always lands on the first settings destination. */
var FIRST_SETTINGS_PATH = "".concat(BASE_PATH, "/import");
/** CZNA-1470 — where ⚙ leads a vocabulary administrator, whose only settings page this is. */
var VOCABULARY_TYPES_SETTINGS_PATH = "".concat(BASE_PATH, "/vocabulary-types");
/** CZNA-1562 — where ⚙ leads a holder of the harvest read who has no other settings page. */
var OAI_HARVEST_SETTINGS_PATH = "".concat(BASE_PATH, "/oai-harvest");
/** Settings sub-routes — being on one of these highlights the ⚙ entry. */
var SETTINGS_PATHS = [
    "".concat(BASE_PATH, "/import"),
    "".concat(BASE_PATH, "/settings"),
    "".concat(BASE_PATH, "/validation-rules"),
    "".concat(BASE_PATH, "/derivation-rules"),
    "".concat(BASE_PATH, "/semantic-catalog"),
    "".concat(BASE_PATH, "/help-texts"),
    "".concat(BASE_PATH, "/vocabulary-types"),
    "".concat(BASE_PATH, "/oai-harvest"),
];
var SECTIONS = [
    // CZNA-972 explicitly removes Návrhy from the rail; CZNA-973 puts cataloguer-owned
    // proposal states under the single Autority screen instead.
    { path: "".concat(BASE_PATH, "/authorities"), labelId: 'ui-czna-authorities.nav.authorities', icon: '📇' },
    { path: "".concat(BASE_PATH, "/headings"), labelId: 'ui-czna-authorities.nav.headings', icon: '🔤' },
    { path: "".concat(BASE_PATH, "/vocabularies"), labelId: 'ui-czna-authorities.nav.vocabularies', icon: '📚' },
    { path: "".concat(BASE_PATH, "/corpus-quality"), labelId: 'ui-czna-authorities.nav.corpusQuality', icon: '📊', perm: CORPUS_QUALITY_CAPABILITY },
];
var RAIL_BG = '#243244';
var RAIL_INK = '#c4d2e2';
var RAIL_ACTIVE_BG = '#2c5cc5';
var RailButton = function (_a) {
    var _b, _c;
    var icon = _a.icon, label = _a.label, active = _a.active, onClick = _a.onClick, ariaLabel = _a.ariaLabel, badge = _a.badge, dataAttr = _a.dataAttr, badgeDataAttr = _a.badgeDataAttr;
    return (React.createElement("button", __assign({ type: "button", onClick: onClick, "aria-current": active ? 'page' : undefined, "aria-label": ariaLabel !== null && ariaLabel !== void 0 ? ariaLabel : label }, (dataAttr ? (_b = {}, _b[dataAttr] = true, _b) : {}), { style: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            width: '100%',
            border: 'none',
            borderRadius: '8px',
            padding: '9px 4px',
            cursor: 'pointer',
            background: active ? RAIL_ACTIVE_BG : 'transparent',
            color: active ? '#fff' : RAIL_INK,
            font: '500 10.5px/1.2 inherit',
            textAlign: 'center',
            position: 'relative',
        } }),
        React.createElement("span", { style: { fontSize: '19px', lineHeight: 1 }, "aria-hidden": true }, icon),
        React.createElement("span", null, label),
        typeof badge === 'number' && badge > 0 ? (React.createElement("span", __assign({}, (badgeDataAttr ? (_c = {}, _c[badgeDataAttr] = true, _c) : {}), { style: {
                position: 'absolute',
                top: '4px',
                right: '14px',
                background: '#fff3cd',
                color: '#856404',
                border: '1px solid #ffe8a1',
                borderRadius: '10px',
                padding: '0 5px',
                fontSize: '9.5px',
                fontWeight: 700,
                minWidth: '16px',
                lineHeight: '15px',
                textAlign: 'center',
            } }), formatReviewCountBadge(badge))) : null));
};
/**
 * CZNA-314 — module-level **activity bar**: a thin, always-visible left rail that
 * holds the top-level navigation (Přehled / Inbox / the catalog sections /
 * ⚙ Nastavení). It is rendered once in `index.js`, outside the route `Switch`, so
 * it stays put across every route — the left menu is part of the GUI and never
 * drills away (M. Mahr, 2026-06-25).
 *
 * <p>This replaces the former in-pane `NavComponent` + `SettingsEntryButton`: the
 * four category buttons no longer share the section's filter pane, which removes
 * the confusing "two competing menus in one pane". Section-local search/filters
 * live in their own pane beside this rail; settings sub-navigation lives in the
 * settings pane (`NavPaneLayout`).
 *
 * <p>CZNA-972 replaces the separate proposal entry with one "Ke kontrole" entry. The
 * endpoint applies the actual role × authority type × review stage × organization scope.
 * The frontend deliberately does not reconstruct that decision from raw role rows (CZNA-598).
 *
 * <p>CZNA-1446 — that entry is the Inbox now, for everyone: what is waiting on me, returned
 * or to review, and its badge counts exactly that. The whole review ("Všechny kontroly")
 * is a separate entry for the administrator only — a supervisor would otherwise see the
 * same work in two places, and the administrator's own work would drown in it.
 */
var ActivityRail = function () {
    var history = useHistory();
    var pathname = useLocation().pathname;
    var intl = useIntl();
    var stripes = useStripes();
    var hasInbox = stripes.hasPerm(INBOX_CAPABILITY);
    var isAdmin = useAuthorizationContext().isAdmin;
    var hasAllReviews = isAdmin && stripes.hasPerm(REVIEW_CAPABILITY);
    var count = useReviewQueueCount(hasInbox).count;
    // CZNA-1596 — "Hlášení od veřejnosti" is for the NK supervisor of the record's type and the
    // authority administrator (1. 11. 2026); the backend decides who that is (canHandle), the
    // rail never rebuilds it from roles (CZNA-598).
    var errorReports = useErrorReportSummary();
    // The route itself opens only with the capability (routesConfig) and shows "Přístup odepřen"
    // without it (CZNA-1627); never lead to a section the user cannot open.
    var showErrorReports = errorReports.canHandle && stripes.hasPerm(ERROR_REPORTS_CAPABILITY);
    var go = function (path) {
        if (pathname !== path)
            history.push(path);
    };
    var isSection = function (path) { return pathname === path || pathname.startsWith("".concat(path, "/")); };
    var isDashboard = pathname === DASHBOARD_PATH || pathname === "".concat(DASHBOARD_PATH, "/");
    var isSettings = SETTINGS_PATHS.some(function (p) { return pathname === p || pathname.startsWith("".concat(p, "/")); });
    // ⚙ is the authority administrator's (CZNA-1448); a vocabulary administrator gets it
    // too, leading straight to Slovníky (CZNA-1470), and so does whoever reads the OAI
    // harvest log, to Sklizeň OAI (CZNA-1562). Nobody else sees it.
    var settingsPath = (stripes.hasPerm(SETTINGS_CAPABILITY) && FIRST_SETTINGS_PATH)
        || (stripes.hasPerm(VOCABULARY_TYPES_CAPABILITY) && VOCABULARY_TYPES_SETTINGS_PATH)
        || (stripes.hasPerm(OAI_HARVEST_CAPABILITY) && OAI_HARVEST_SETTINGS_PATH)
        || null;
    var label = function (id) { return intl.formatMessage({ id: id }); };
    var inboxLabel = label('ui-czna-authorities.nav.inbox');
    return (React.createElement("nav", { "aria-label": label('ui-czna-authorities.nav.aria.main'), "data-test-activity-rail": true, style: {
            width: '92px',
            flexShrink: 0,
            height: '100%',
            background: RAIL_BG,
            display: 'flex',
            flexDirection: 'column',
            padding: '10px 8px',
            gap: '4px',
            overflowY: 'auto',
            boxSizing: 'border-box',
        } },
        React.createElement(RailButton, { icon: "\uD83C\uDFE0", label: label('ui-czna-authorities.nav.dashboard'), active: isDashboard, onClick: function () { return go(DASHBOARD_PATH); }, dataAttr: "data-test-rail-dashboard" }),
        React.createElement("div", { style: { height: '1px', background: 'rgba(255,255,255,0.12)', margin: '6px 6px' } }),
        hasInbox ? (React.createElement(RailButton, { icon: "\uD83D\uDCE5", label: inboxLabel, active: isSection(INBOX_PATH), onClick: function () { return go(INBOX_PATH); }, ariaLabel: count > 0
                ? intl.formatMessage({ id: 'ui-czna-authorities.nav.inbox.aria' }, { count: count })
                : inboxLabel, badge: count, dataAttr: "data-test-rail-inbox", badgeDataAttr: "data-test-rail-inbox-count" })) : null,
        hasAllReviews ? (React.createElement(RailButton, { icon: "\u2705", label: label('ui-czna-authorities.nav.reviewQueue'), active: isSection(REVIEW_PATH), onClick: function () { return go(REVIEW_PATH); }, dataAttr: "data-test-rail-review" })) : null,
        SECTIONS.filter(function (s) { return !s.perm || stripes.hasPerm(s.perm); }).map(function (s) { return (React.createElement(RailButton, { key: s.path, icon: s.icon, label: label(s.labelId), active: isSection(s.path), onClick: function () { return go(s.path); } })); }),
        showErrorReports ? (React.createElement(RailButton, { icon: "\uD83D\uDCE3", label: label('ui-czna-authorities.nav.errorReports'), active: isSection(ERROR_REPORTS_PATH), onClick: function () { return go(ERROR_REPORTS_PATH); }, ariaLabel: errorReports.newCount > 0
                ? intl.formatMessage({ id: 'ui-czna-authorities.dashboard.tile.errorReports.aria' }, { count: errorReports.newCount })
                : undefined, badge: errorReports.newCount, dataAttr: "data-test-rail-error-reports", badgeDataAttr: "data-test-rail-error-reports-count" })) : null,
        settingsPath ? (React.createElement(React.Fragment, null,
            React.createElement("div", { style: { marginTop: 'auto' } }),
            React.createElement("div", { style: { height: '1px', background: 'rgba(255,255,255,0.12)', margin: '6px 6px' } }),
            React.createElement(RailButton, { icon: "\u2699", label: label('ui-czna-authorities.navigation.settings'), active: isSettings, onClick: function () { return go(settingsPath); }, dataAttr: "data-test-rail-settings" }))) : null));
};
export default ActivityRail;
